import type { TFunction } from 'i18next';

import type { LocationModel } from '@/entities/location';
import type { MappingBreakdownResponse, TopItem } from '@/entities/dashboard';
import { BREAKDOWN_TYPES, type BreakdownType } from '@/entities/dashboard/mapping';

import type { LocationWithData } from '../model/map-provider.interface';

export const BREAKDOWN_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export const DATA_LAYER_LABELS = {
  stock: {
    key: 'hover.stockOnHand',
    default: 'Stock on Hand'
  },
  releases: {
    key: 'map.popup.releases',
    default: 'Releases'
  },
  requests: {
    key: 'map.popup.requests',
    default: 'Requests'
  }
} as const;

export type DataLayer = keyof typeof DATA_LAYER_LABELS;

export function getDataLayerLabel(layer: DataLayer, t?: TFunction): string {
  const config = DATA_LAYER_LABELS[layer];
  if (!config) {
    const defaultKey = 'map.popup.data';
    const defaultLabel = 'Data';
    return t ? t(defaultKey, defaultLabel) : defaultLabel;
  }
  return t ? t(config.key, config.default) : config.default;
}

export function extractLocationData(location: LocationModel | LocationWithData) {
  const dataValue = 'dataValue' in location ? (location.dataValue ?? 0) : 0;
  const hasData = 'dataValue' in location && dataValue > 0;
  const dataProgress = 'dataProgress' in location ? location.dataProgress || [] : [];

  return { dataValue, hasData, dataProgress };
}

export function getBreakdownType(dataLayer: DataLayer): BreakdownType {
  return dataLayer === 'stock' ? BREAKDOWN_TYPES.BY_ORGANIZATION : BREAKDOWN_TYPES.BY_PRODUCT;
}

export function calculateWeekNumber(currentWeek: number): number | undefined {
  return currentWeek !== 0 ? Math.abs(currentWeek) : undefined;
}

export function formatPieChartData(
  breakdownData: MappingBreakdownResponse | undefined,
  colors: string[] = BREAKDOWN_COLORS
): Array<{ name: string; value: number; color: string }> {
  if (!breakdownData?.breakdown?.series) return [];

  return breakdownData.breakdown.series.slice(0, 5).map((item: TopItem, index: number) => ({
    name: item.name,
    value: item.percentage,
    color: item.color || colors[index % colors.length]
  }));
}

export function formatProgressData(dataProgress?: number[] | null): Array<{ week: string; value: number }> {
  if (!dataProgress || dataProgress.length === 0) return [];

  return dataProgress.slice(-12).map((value, index) => ({
    week: `W${-12 + index}`,
    value: Math.abs(Math.round(value * 100) / 100)
  }));
}

export function calculateChange(
  currentValue: number,
  previousValue: number
): {
  value: number;
  isPositive: boolean;
  isNegative: boolean;
} {
  const change = currentValue - previousValue;
  return {
    value: change,
    isPositive: change > 0,
    isNegative: change < 0
  };
}
